import { useState, useCallback, useEffect } from 'react';

/**
 * Clé localStorage pour les tooltips vus
 */
const TOOLTIPS_STORAGE_KEY = 'b8ild_tooltips_seen';

/**
 * IDs des tooltips disponibles dans l'application
 */
export type TooltipId =
  | 'dashboard_overview'
  | 'create_chantier'
  | 'add_devis'
  | 'team_assignment'
  | 'planning_calendar'
  | 'expenses_ocr'
  | 'profitability_chart'
  | 'ai_analysis'
  | 'project_card'
  | 'pilotage_panel';

/**
 * Configuration d'un tooltip
 */
export interface TooltipConfig {
  id: TooltipId;
  title: string;
  content: string;
  position?: 'top' | 'bottom' | 'left' | 'right';
}

/**
 * Tooltips par défaut de l'application
 */
export const TOOLTIPS: Record<TooltipId, TooltipConfig> = {
  dashboard_overview: {
    id: 'dashboard_overview',
    title: 'Bienvenue sur votre tableau de bord',
    content: 'Ici vous retrouvez tous vos KPIs et alertes en un coup d\'œil.',
    position: 'bottom',
  },
  create_chantier: {
    id: 'create_chantier',
    title: 'Créer un chantier',
    content: 'Commencez par créer votre premier chantier avec son devis.',
    position: 'bottom',
  },
  add_devis: {
    id: 'add_devis',
    title: 'Ajouter un devis',
    content: 'Le devis définit le budget HT de votre chantier.',
    position: 'right',
  },
  team_assignment: {
    id: 'team_assignment',
    title: 'Affecter l\'équipe',
    content: 'Assignez vos ouvriers au chantier pour calculer les coûts.',
    position: 'left',
  },
  planning_calendar: {
    id: 'planning_calendar',
    title: 'Planning',
    content: 'Visualisez toutes vos affectations dans le calendrier.',
    position: 'bottom',
  },
  expenses_ocr: {
    id: 'expenses_ocr',
    title: 'Scan IA',
    content: 'Photographiez vos factures, l\'IA extrait les données automatiquement.',
    position: 'top',
  },
  profitability_chart: {
    id: 'profitability_chart',
    title: 'Rentabilité',
    content: 'Suivez l\'évolution de votre marge en temps réel.',
    position: 'left',
  },
  ai_analysis: {
    id: 'ai_analysis',
    title: 'Analyse IA',
    content: 'Obtenez des recommandations personnalisées pour améliorer votre rentabilité.',
    position: 'bottom',
  },
  project_card: {
    id: 'project_card',
    title: 'Carte chantier',
    content: 'Cliquez pour accéder aux détails et à la gestion financière.',
    position: 'right',
  },
  pilotage_panel: {
    id: 'pilotage_panel',
    title: 'Centre de pilotage',
    content: 'Vos alertes et actions importantes sont ici.',
    position: 'left',
  },
};

/**
 * Hook pour gérer les tooltips contextuels
 * Sera implémenté complètement dans le Prompt 6
 */
export function useTooltips() {
  const [seenTooltips, setSeenTooltips] = useState<Set<TooltipId>>(new Set());
  const [activeTooltip, setActiveTooltip] = useState<TooltipId | null>(null);
  const [isEnabled, setIsEnabled] = useState(true);

  // Charger les tooltips vus depuis localStorage
  useEffect(() => {
    const saved = localStorage.getItem(TOOLTIPS_STORAGE_KEY);
    if (saved) {
      try {
        const parsed = JSON.parse(saved);
        setSeenTooltips(new Set(parsed.seen || []));
        setIsEnabled(parsed.enabled !== false);
      } catch (e) {
        // Ignorer
      }
    }
  }, []);

  /**
   * Sauvegarde l'état dans localStorage
   */
  const saveState = useCallback((seen: Set<TooltipId>, enabled: boolean) => {
    localStorage.setItem(TOOLTIPS_STORAGE_KEY, JSON.stringify({
      seen: Array.from(seen),
      enabled,
    }));
  }, []);

  /**
   * Vérifie si un tooltip a déjà été vu
   */
  const hasSeenTooltip = useCallback((id: TooltipId): boolean => {
    return seenTooltips.has(id);
  }, [seenTooltips]);

  /**
   * Marque un tooltip comme vu
   */
  const markAsSeen = useCallback((id: TooltipId) => {
    setSeenTooltips(prev => {
      const updated = new Set(prev);
      updated.add(id);
      saveState(updated, isEnabled);
      return updated;
    });
    if (activeTooltip === id) {
      setActiveTooltip(null);
    }
  }, [activeTooltip, isEnabled, saveState]);

  /**
   * Affiche un tooltip
   */
  const showTooltip = useCallback((id: TooltipId) => {
    if (isEnabled && !seenTooltips.has(id)) {
      setActiveTooltip(id);
    }
  }, [isEnabled, seenTooltips]);

  /**
   * Ferme le tooltip actif
   */
  const hideTooltip = useCallback(() => {
    if (activeTooltip) {
      markAsSeen(activeTooltip);
    }
    setActiveTooltip(null);
  }, [activeTooltip, markAsSeen]);

  /**
   * Active/désactive les tooltips
   */
  const toggleTooltips = useCallback((enabled: boolean) => {
    setIsEnabled(enabled);
    saveState(seenTooltips, enabled);
    if (!enabled) {
      setActiveTooltip(null);
    }
  }, [seenTooltips, saveState]);

  /**
   * Réinitialise tous les tooltips (pour "revoir les tutoriels")
   */
  const resetTooltips = useCallback(() => {
    setSeenTooltips(new Set());
    setIsEnabled(true);
    localStorage.removeItem(TOOLTIPS_STORAGE_KEY);
  }, []);

  /**
   * Récupère la config d'un tooltip
   */
  const getTooltipConfig = useCallback((id: TooltipId): TooltipConfig | null => {
    return TOOLTIPS[id] || null;
  }, []);

  return {
    // État
    seenTooltips,
    activeTooltip,
    isEnabled,

    // Vérifications
    hasSeenTooltip,

    // Actions
    showTooltip,
    hideTooltip,
    markAsSeen,
    toggleTooltips,
    resetTooltips,

    // Config
    getTooltipConfig,
  };
}
